import React, { useCallback, useEffect, useState } from "react";
import { api } from "../lib/apiClient";
import { toast } from "sonner";
import { RefreshCw, Undo2 } from "lucide-react";

const eur = (c) => `€${((c || 0) / 100).toFixed(2)}`;

export const AdminReviews = () => {
  const [data, setData] = useState(null);
  const [rate, setRate] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [r, s] = await Promise.all([api.get("/admin/review-requests"), api.get("/admin/marketplace-settings")]);
      setData(r.data);
      setRate(String(Math.round(s.data.platform_commission_rate * 100)));
    } catch { toast.error("Could not load"); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveRate = async () => {
    setBusy(true);
    try {
      await api.put("/admin/marketplace-settings", { platform_commission_rate: Number(rate) / 100 });
      toast.success("Commission updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const refund = async (id) => {
    if (!window.confirm("Full refund + reverse the consultant transfer?")) return;
    setBusy(true);
    try {
      await api.post(`/admin/review-requests/${id}/refund`, { reason: "admin_refund" });
      toast.success("Refunded");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Refund failed"); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="text-[#94A3B8]">Loading…</div>;

  return (
    <div className="space-y-4" data-testid="admin-reviews-panel">
      <div className="bg-white border border-slate-200 rounded-xl px-6 py-4 flex items-center gap-4 flex-wrap">
        <p className="font-display font-extrabold text-[#0B1B3D]">Platform commission</p>
        <div className="flex items-center gap-2">
          <input data-testid="commission-rate-input" type="number" min="0" max="50" value={rate} onChange={(e) => setRate(e.target.value)}
            className="w-20 border border-slate-200 rounded-lg px-3 py-1.5 text-sm" /> <span className="text-sm text-[#475569]">%</span>
          <button data-testid="save-commission-btn" disabled={busy} onClick={saveRate}
            className="bg-[#0B1B3D] text-white px-3 py-1.5 rounded-lg text-xs font-bold">Save</button>
        </div>
        <p className="text-xs text-[#94A3B8]">Single source of truth — applied server-side to new review requests.</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h2 className="font-display font-extrabold text-[#0B1B3D]">Review requests</h2>
          <button onClick={load} className="text-sm font-bold text-[#475569] inline-flex items-center gap-1.5"><RefreshCw className="w-3.5 h-3.5" /> Refresh</button>
        </div>
        {data.review_requests.length === 0 ? <p className="p-6 text-sm text-[#94A3B8]">No review requests yet.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-reviews-table">
              <thead><tr className="text-left font-mono-kz text-xs text-[#94A3B8] uppercase tracking-widest">
                <th className="p-3">Date</th><th className="p-3">Customer</th><th className="p-3">Consultant</th><th className="p-3">Report</th>
                <th className="p-3">Amount</th><th className="p-3">Fee</th><th className="p-3">Consultant €</th><th className="p-3">Review</th><th className="p-3">Payment</th><th className="p-3"></th>
              </tr></thead>
              <tbody>
                {data.review_requests.map((rr) => (
                  <tr key={rr.review_request_id} className="border-t border-slate-100">
                    <td className="p-3 whitespace-nowrap text-[#475569]">{new Date(rr.created_at).toLocaleDateString()}</td>
                    <td className="p-3 font-mono-kz text-xs text-[#475569] max-w-[140px] truncate">{rr.customer_email}</td>
                    <td className="p-3 font-bold text-[#0B1B3D]">{rr.consultant_name}</td>
                    <td className="p-3 text-[#475569] max-w-[160px] truncate">{rr.session_title}</td>
                    <td className="p-3 font-bold">{eur(rr.service_amount_cents)}</td>
                    <td className="p-3 text-[#0EA5E9]">{eur(rr.platform_fee_cents)}</td>
                    <td className="p-3 text-[#10B981]">{eur(rr.consultant_amount_cents)}</td>
                    <td className="p-3 font-mono-kz text-xs">{rr.review_status}</td>
                    <td className="p-3 font-mono-kz text-xs">{rr.payment_status}{rr.dispute_status ? ` · ⚠ ${rr.dispute_status}` : ""}</td>
                    <td className="p-3">
                      {rr.payment_status === "PAID" && (
                        <button data-testid={`refund-${rr.review_request_id}`} disabled={busy} onClick={() => refund(rr.review_request_id)}
                          className="border border-[#EF4444]/40 text-[#EF4444] px-2.5 py-1 rounded-md text-xs font-bold inline-flex items-center gap-1"><Undo2 className="w-3 h-3" /> Refund</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
